<template>
  <section
    :class="[
      'flex flex-col items-end gap-y-1 w-full @container',
      { 'backlight-animation': light },
    ]"
    :data-timestamp="event.timestamp"
    aria-label="Outgoing chat message"
    data-test="chat-message-outgoing"
  >
    <section
      v-if="hasForwarding && shouldRenderHeavy && !isMessageDeleted"
      class="relative rounded-12 bg-comp-messages-bubble-bg-out-def chat-message-max-w w-fit min-w-0"
      data-test="chat-message-outgoing-forward-bubble"
    >
      <forward-message
        :id="event.eventId"
        class="min-w-0 max-w-full"
        :class="useForwardMetaSlot ? '' : 'mb-2'"
        :forwarding="event.item.forwarding!"
        :event="event"
        :can-expand-text="true"
      >
        <template
          v-if="useForwardMetaSlot"
          #meta
        >
          <chat-event-bubble-meta
            :time="hhmm"
            :is-edited="!!isEdited"
            :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
            :is-pinned="!!isPinned"
            :show-status="true"
            :event="event"
            :is-group="!!room?.isGroup"
            :contact-id="event.item.pServed"
            :show-error="true"
          />
        </template>
      </forward-message>
      <div
        v-if="showForwardFooter"
        :class="footerClass"
      >
        <div
          v-if="isMetaInlineWithReactions"
          class="flex w-full min-w-0 flex-wrap items-end gap-0.5"
        >
          <message-reactions
            :event-reactions="eventReactions!"
            :is-show-reaction-info="room?.isGroup ?? false"
            as-contents
          />
          <chat-event-bubble-meta
            class="ms-auto shrink-0 self-end"
            :time="hhmm"
            :is-edited="!!isEdited"
            :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
            :is-pinned="!!isPinned"
            :show-status="true"
            :event="event"
            :is-group="!!room?.isGroup"
            :contact-id="event.item.pServed"
            :show-error="true"
          />
        </div>
        <template v-else>
          <message-reactions
            v-if="shouldRenderHeavy && eventReactions"
            :event-reactions="eventReactions"
            :is-show-reaction-info="room?.isGroup ?? false"
            class="w-full min-w-0"
          />
          <div class="flex items-center justify-end">
            <chat-event-bubble-meta
              :time="hhmm"
              :is-edited="!!isEdited"
              :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
              :is-pinned="!!isPinned"
              :show-status="true"
              :event="event"
              :is-group="!!room?.isGroup"
              :contact-id="event.item.pServed"
              :show-error="true"
            />
          </div>
        </template>
      </div>
    </section>

    <section
      v-if="hasOwnContent"
      class="relative rounded-12 bg-comp-messages-bubble-bg-out-def chat-message-max-w w-fit min-w-0"
      data-test="chat-message-outgoing-content-bubble"
    >
      <template
        v-if="
          shouldRenderHeavy &&
            !isMessageDeleted &&
            (quotedEvent?.item.messageText || !!quotedEvent?.item.files?.length ||
              (quotedEvent?.item.forwarding && quotedEvent?.item.forwarding.forwardingEventId))"
      >
        <quoted-message
          :id="event.eventId"
          :class="quoteBlockClass"
          :event="quotedEvent"
          :selected-text="event.item.quote?.quotedText!"
          :is-clickable="true"
          :can-expand-text="true"
          @click="scrollToQuotedEvent"
        />
      </template>
      <div
        v-else-if="!shouldRenderHeavy && !isMessageDeleted && event.item.quote?.quotedEventId"
        :class="quoteBlockClass"
        class="w-full"
        aria-hidden="true"
      />
      <vcard-in-message
        v-if="shouldRenderHeavy && isSingleVCardFile && !!event.item.files?.[0]"
        class="m-3"
        :file="event.item.files?.[0]"
      >
        <template #add-to-chat="{ pServed }">
          <add-contact-to-chats-button
            v-if="pServed"
            :p-served="pServed"
          >
            <template #default="{ open }">
              <button
                type="button"
                class="min-w-0 flex-1 text-center text-label-m-13-18 text-comp-link-neut-def"
                data-test="vcard-add-to-chat-button"
                @click.stop="open"
              >
                {{ $t('chat.addToChat') }}
              </button>
            </template>
          </add-contact-to-chats-button>
        </template>
      </vcard-in-message>
      <chat-event-list-item-file-list
        v-else-if="shouldRenderHeavy && event.item?.files?.length"
        :files="event.item?.files ?? []"
        :type="'OutgoingMessage'"
        :is-pinned="isPinned"
        :time="hhmm"
        :is-group="!!room?.isGroup"
        :event="event"
        :class="isVoice ? '' : 'mb-1'"
        @images-loaded="emit('resize')"
      />
      <div
        v-else-if="!shouldRenderHeavy && isSingleVCardFile"
        class="m-2 w-full min-h-[160px]"
        aria-hidden="true"
      />
      <div
        v-else-if="!shouldRenderHeavy && event.item?.files?.length"
        class="w-full"
        :style="{ minHeight: '120px' }"
        aria-hidden="true"
      />
      <i
        v-if="isMessageDeleted"
        class="text-1316 px-2 py-2"
      >{{ $t('chat.messageRemoved') }}</i>
      <div
        v-else-if="messageText"
        class="flow-root min-w-0 max-w-full wrap-break-word px-2 pt-2 text-fg-base-fg3"
        :class="isMetaInlineWithText ? 'pb-2' : 'pb-1'"
        @dblclick.stop
      >
        <span
          ref="textElement"
          data-test="chat-event-outgoing-message-container"
          class="
            select-text whitespace-pre-wrap
            [&_a]:text-comp-link-neut-def
            [&_b[data-contact-uri]]:cursor-pointer [&_b[data-contact-uri]]:font-normal [&_b[data-contact-uri]]:text-surf-brand-basedarkblue-def
          "
          :class="messageClasses"
          v-html="messageText"
        />
        <chat-event-bubble-meta
          v-if="isMetaInlineWithText"
          class="float-right relative ms-2"
          :time="hhmm"
          :is-edited="!!isEdited && !isMessageDeleted"
          :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
          :is-pinned="!!isPinned"
          :show-status="!isMessageDeleted"
          :event="event"
          :is-group="!!room?.isGroup"
          :contact-id="event.item.pServed"
          :show-error="true"
        />
      </div>
      <div
        v-if="isTextTruncated"
        class="mx-2 mt-1 flex cursor-pointer items-center gap-1 pr-1"
        @click="toggleExpand"
      >
        <p class="text-body-r-14-20 text-comp-link-neut-def hover:underline">
          {{ isExpanded ? $t('common.collapse') : $t('common.expand') }}
        </p>
        <wui-icon
          :name="isExpanded ? 'chevronUpSmM' : 'chevronDownSmM'"
          class="text-comp-link-neut-def h-5! w-5!"
        />
      </div>

      <div
        v-if="showFooter"
        :class="footerClass"
      >
        <div
          v-if="isMetaInlineWithReactions"
          class="flex w-full min-w-0 flex-wrap items-end gap-0.5"
        >
          <message-reactions
            :event-reactions="eventReactions!"
            :is-show-reaction-info="room?.isGroup ?? false"
            as-contents
          />
          <chat-event-bubble-meta
            class="ms-auto shrink-0 self-end"
            :time="hhmm"
            :is-edited="!!isEdited && !isMessageDeleted"
            :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
            :is-pinned="!!isPinned"
            :show-status="!isMessageDeleted"
            :event="event"
            :is-group="!!room?.isGroup"
            :contact-id="event.item.pServed"
            :show-error="true"
          />
        </div>
        <template v-else>
          <message-reactions
            v-if="shouldRenderHeavy && eventReactions"
            :event-reactions="eventReactions"
            :is-show-reaction-info="room?.isGroup ?? false"
            class="w-full min-w-0"
          />
          <div
            v-if="showFooterMeta"
            class="flex items-center justify-end"
          >
            <chat-event-bubble-meta
              :time="hhmm"
              :is-edited="!!isEdited && !isMessageDeleted"
              :edited-title="$t('chat.editedTimes') + ': ' + event.item.era"
              :is-pinned="!!isPinned"
              :show-status="!isMessageDeleted"
              :event="event"
              :is-group="!!room?.isGroup"
              :contact-id="event.item.pServed"
              :show-error="true"
            />
          </div>
        </template>
      </div>
    </section>
  </section>
</template>

<script lang="ts" setup>
import { WuiIcon } from '@wui/common-library'
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, PinnedMessage } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, onMounted, toRefs } from 'vue'

import { AddContactToChatsButton } from '@/features/add-contact-to-chats'
import { MessageReactions } from '@/features/message-reaction'

import { ChatEventListItemFileList } from '@/entities/chat-event-file'
import { useChatEvent } from '@/entities/chat-handlers'
import { usePinEventsStore, ForwardMessage, QuotedMessage, ChatEventBubbleMeta, VcardInMessage } from '@/entities/messages'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreRooms } from '@/entities/room'
import { isVoiceMessage, useVoiceMessageStore } from '@/entities/voice-message'

import { LINES_COUNT_TO_CLAMP_MESSAGE, useTextExpand } from '@/shared/composables/use-text-expand'
import { formatMessageText } from '@/shared/utils/text-entities'
import { isVCardFile } from '@/shared/utils/vcard'

const props = withDefaults(defineProps<{
  event: HistoryMessageItem<MessageOutgoingEvent>
  next?: HistoryMessageItem<MessageOutgoingEvent> | undefined
  quotedEvent?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
  small?: boolean
  unread?: boolean
  light?: boolean
  isEdited?: boolean
  isMessageDeleted: boolean
  /** When false — skip files/quotes/reactions until near viewport (faster first measure). */
  renderHeavy?: boolean
}>(), {
  next: undefined,
  quotedEvent: undefined,
  small: false,
  unread: false,
  light: false,
  isEdited: false,
  /** Default true for callers outside the virtualized chat list (Vue Boolean-casts omitted → false). */
  renderHeavy: true,
})

const shouldRenderHeavy = computed(() => props.renderHeavy)

const emit = defineEmits<{
  (event: 'scrollToItem', targetEventId: number, sourceEventId?: number): void
  (event: 'resize'): void
}>()

onMounted(() => {
  initTextExpanding()
})

const { event, next, isMessageDeleted } = toRefs(props)

const { hhmm, messageClasses } = useChatEvent(event, next)
const { reactionEvents } = storeToRefs(useReactionsStore())
const { transcripts, expandedTranscripts } = storeToRefs(useVoiceMessageStore())
const eventReactions = computed(() => reactionEvents.value.get(event.value.eventId ?? 0))
const { getPinEventsForRoom } = usePinEventsStore()
const pinEvents = computed(() => getPinEventsForRoom(props.event.item.pServed))

const { findByPServed } = useStoreRooms()

const room = computed(() => findByPServed(props.event.item.pServed))
const scrollToQuotedEvent = () => {
  if (props.quotedEvent?.eventId && props.event.eventId) {
    emit('scrollToItem', props.quotedEvent.eventId, props.event.eventId)
  }
}

const messageText = computed((): string => (
  formatMessageText(event.value.item.messageText, event.value.item.entities)
))

const quoteBlockClass = computed(() => {
  const textFollowsQuote = Boolean(messageText.value) && !event.value.item.files?.length
  return [
    'mx-2 mt-2 min-h-[54px]',
    textFollowsQuote ? '' : 'mb-2',
  ]
})

const hasForwarding = computed(() => Boolean(event.value.item.forwarding?.forwardingEventId))

const hasOwnContent = computed(() => {
  if (isMessageDeleted.value) {
    return true
  }
  if (messageText.value) {
    return true
  }
  if (event.value.item.files?.length) {
    return true
  }
  if (
    props.quotedEvent?.item.messageText ||
    !!props.quotedEvent?.item.files?.length ||
    (props.quotedEvent?.item.forwarding && props.quotedEvent.item.forwarding.forwardingEventId)
  ) {
    return true
  }
  return false
})

const {
  isExpanded,
  textElement,
  isTextTruncated,
  initTextExpanding,
  toggleExpand,
} = useTextExpand(messageText, LINES_COUNT_TO_CLAMP_MESSAGE)

const isPinned = computed(() => {
  return pinEvents.value?.find(i => i.eventId === props.event.eventId) as PinnedMessage
})

const isSingleVCardFile = computed(() => {
  const files = props.event.item.files
  return files?.length === 1 && isVCardFile(files[0]!)
})

const isVoice = computed(() => !isMessageDeleted.value && isVoiceMessage(event.value.item))
const hasVoiceTranscript = computed(() => {
  const cookie = event.value.item.files?.[0]?.filecookie
  return Boolean(cookie && transcripts.value.get(cookie) && expandedTranscripts.value.has(cookie))
})

const hasReactions = computed(() => Boolean(shouldRenderHeavy.value && eventReactions.value))
const isEmojiOnlyMessage = computed(() => messageClasses?.value?.includes('text-emoji-r-80-80') ?? false)
const hasForwardingText = computed(() => Boolean(event.value.item.forwarding?.forwardingText))

const isMetaInlineWithText = computed(() => (
  !isVoice.value
  && !hasReactions.value
  && Boolean(messageText.value)
  && !isMessageDeleted.value
  && !isEmojiOnlyMessage.value
  && !(isTextTruncated.value && !isExpanded.value)
))

const isMetaInlineWithReactions = computed(() => !isVoice.value && hasReactions.value)

/** Forward-only bubble: meta lives in ForwardMessage slot (inline with text when possible). */
const useForwardMetaSlot = computed(() => (
  !hasOwnContent.value
  && !hasReactions.value
  && hasForwardingText.value
))

const showForwardFooter = computed(() => (
  !hasOwnContent.value
  && (hasReactions.value || !hasForwardingText.value)
))

const showFooterMeta = computed(() => !isMetaInlineWithText.value && !isMetaInlineWithReactions.value)
const showFooter = computed(() => hasReactions.value || showFooterMeta.value || isVoice.value)

const footerClass = computed(() => {
  if (isVoice.value) {
    if (hasVoiceTranscript.value) return 'flex flex-col items-stretch gap-y-1 px-2 pb-1.5'
    return 'relative z-1 -mt-4 flex flex-col items-stretch gap-y-1 px-2 pb-1.5'
  }
  if (isMetaInlineWithReactions.value) return 'flex flex-col items-stretch px-2 pb-2'
  return 'flex flex-col items-stretch gap-y-2 px-2 pb-2'
})
</script>
